import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { API, apiClient as axios } from "../../lib/api";
import { Toast, Pagination } from "../../components/ui";
import LeadDetailsModal from "../../components/LeadDetailsModal";
import { Clock, CheckCircle, XCircle } from "lucide-react";
import Toolbar from "./components/Toolbar";
import LeadsTable from "./components/LeadsTable";

export default function AdminDashboard({ token }) {
  const [leads, setLeads] = useState([]);
  const [filters, setFilters] = useState({ segment: "", status: "" });
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState({});
  const [rejecting, setRejecting] = useState({});
  const [showApproveDialog, setShowApproveDialog] = useState({});
  const [showRejectDialog, setShowRejectDialog] = useState({});
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetails, setShowLeadDetails] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadLeads = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.segment) params.append("segment", filters.segment);
      if (filters.status) params.append("status", filters.status);
      params.append("page", page.toString());
      params.append("limit", "10");
      const url = `${API}/leads?${params.toString()}`;
      const res = await axios.get(url, { headers: authHeaders });
      setLeads(res.data?.data || []);
      setTotalPages(res.data?.totalPages || 1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load leads");
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filters.segment, filters.status, page]);

  useEffect(() => {
    setPage(1);
  }, [filters.segment, filters.status]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const approve = async (id) => {
    try {
      setApproving(prev => ({ ...prev, [id]: true }));
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status: 'approved' } : l));
      await axios.patch(`${API}/leads/${id}/approve`, {}, { headers: authHeaders });
      toast.success("Lead approved successfully!");
      loadLeads();
    } catch (e) {
      toast.error("Failed to approve lead");
    } finally {
      setApproving(prev => ({ ...prev, [id]: false }));
      setShowApproveDialog(prev => ({ ...prev, [id]: false }));
    }
  };

  const reject = async (id) => {
    try {
      setRejecting(prev => ({ ...prev, [id]: true }));
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status: 'rejected' } : l));
      await axios.patch(`${API}/leads/${id}/reject`, {}, { headers: authHeaders });
      toast.success("Lead rejected successfully!");
      loadLeads();
    } catch (e) {
      toast.error("Failed to reject lead");
    } finally {
      setRejecting(prev => ({ ...prev, [id]: false }));
      setShowRejectDialog(prev => ({ ...prev, [id]: false }));
    }
  };

  const clearFilters = () => {
    setFilters({ segment: "", status: "" });
    setPage(1);
  };

  const handleRowClick = useCallback((lead) => {
    setSelectedLead(lead);
    setShowLeadDetails(true);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="h-3 w-3 mr-1" />;
      case 'approved': return <CheckCircle className="h-3 w-3 mr-1" />;
      case 'rejected': return <XCircle className="h-3 w-3 mr-1" />;
      default: return null;
    }
  };

  return (
    <div className="page">
      <Toast />
      <LeadDetailsModal
        lead={selectedLead}
        isOpen={showLeadDetails}
        onClose={() => setShowLeadDetails(false)}
      />
      <Toolbar
        filters={filters}
        setFilters={setFilters}
        loading={loading}
        onRefresh={loadLeads}
        onClearFilters={clearFilters}
      />
      <LeadsTable
        loading={loading}
        leads={leads}
        onRowClick={handleRowClick}
        approving={approving}
        rejecting={rejecting}
        showApproveDialog={showApproveDialog}
        showRejectDialog={showRejectDialog}
        onApprove={approve}
        onReject={reject}
        onOpenApproveDialog={(id, open) => setShowApproveDialog(prev => ({ ...prev, [id]: open }))}
        onOpenRejectDialog={(id, open) => setShowRejectDialog(prev => ({ ...prev, [id]: open }))}
      />
      {!loading && leads?.length > 0 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          loading={loading}
        />
      )}
    </div>
  );
}


